import React from 'react';
import { Box, Stack } from '@mui/material';
import type { Theme } from '@mui/material/styles';

type Platform = 'mac' | 'windows' | 'linux' | 'auto';

type Props = {
  keys: string[];
  platform?: Platform; // default: auto
};

const isMacLike = () => {
  if (typeof navigator === 'undefined') return false;
  const p = navigator.platform?.toLowerCase() ?? '';
  const ua = navigator.userAgent?.toLowerCase() ?? '';
  return p.includes('mac') || p.includes('iphone') || p.includes('ipad') || ua.includes('mac os');
};

const mapKey = (raw: string, platform: Exclude<Platform, 'auto'>): string => {
  const k = raw.trim().toLowerCase();

  if (['cmd', 'command', 'meta', 'super', 'win', 'windows'].includes(k)) {
    return platform === 'mac' ? '⌘' : 'Ctrl';
  }
  if (['ctrl', 'control'].includes(k)) return platform === 'mac' ? '⌃' : 'Ctrl';
  if (['alt', 'option', 'opt'].includes(k)) return platform === 'mac' ? '⌥' : 'Alt';
  if (k === 'shift') return platform === 'mac' ? '⇧' : 'Shift';
  if (['enter', 'return'].includes(k)) return platform === 'mac' ? '↩' : 'Enter';
  if (['tab'].includes(k)) return platform === 'mac' ? '⇥' : 'Tab';
  if (['up', 'arrowup'].includes(k)) return '↑';
  if (['down', 'arrowdown'].includes(k)) return '↓';
  if (['left', 'arrowleft'].includes(k)) return '←';
  if (['right', 'arrowright'].includes(k)) return '→';

  return k.length === 1 ? k.toUpperCase() : raw;
};

const Keycap = ({ text, size }: { text: string; size?: number }) => (
  <Box
    component="kbd"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      p: 0.5,
      minWidth: size ? size : 24,
      borderRadius: '4px',

      // minimal dark pill style like screenshot
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)',
      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.15)'}`,

      color: theme.palette.text.secondary,
      fontSize: size ? size / 2 : 12, // 12px
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

      // reset default kbd look
      margin: 0,
      boxShadow: 'none',
      userSelect: 'none',
      whiteSpace: 'nowrap',
    })}
  >
    {text}
  </Box>
);

const KeyboardCmd = ({ keys, platform = 'auto' }: Props) => {
  const resolvedPlatform: Exclude<Platform, 'auto'> =
    platform === 'auto' ? (isMacLike() ? 'mac' : 'windows') : platform;

  const rendered = keys.map((k) => mapKey(k, resolvedPlatform));

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {rendered.map((k, i) => (
        <React.Fragment key={`${k}-${i}`}>
          <Keycap text={k} size={20} />
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default KeyboardCmd;